import { ChangeEvent, JSX, ReactElement, useState } from 'react';
import {
    Datagrid,
    DateField,
    FunctionField,
    Pagination,
    ReferenceManyField,
    TextField,
    useRecordContext,
} from 'react-admin';
import { Box, Chip, MenuItem, TextField as MuiTextField, Typography } from '@mui/material';
import { CustomerAccount } from '../../../../types/entities';

type ActivityChange = {
    field: string,
    /** Served by the API, so HQ and the customer portal cannot word the same field differently. */
    label: string,
    from: unknown,
    to: unknown
};

type ActivityContextEntry = {
    key: string,
    label: string,
    value: unknown
};

type ActivityRecord = {
    id: number,
    typeLabel: string,
    category: string,
    actorName: string,
    actorRole: string | null,
    actorRoleLabel: string | null,
    subjectLabel: string | null,
    subjectType: string | null,
    subjectId: number | null,
    changeSet: ActivityChange[],
    contextEntries: ActivityContextEntry[],
    createdAt: string
};

/**
 * The categories the activity monitor files events under. Kept in step with
 * CustomerActivityType::categories() on the API — this is a filter dropdown, so a stale entry
 * offers a filter that returns nothing rather than breaking anything.
 */
const activityCategories: { id: string, name: string }[] = [
    { id: 'account', name: 'Account' },
    { id: 'security', name: 'Security' },
    { id: 'subscription', name: 'Subscription' },
    { id: 'organization', name: 'Organizations' },
    { id: 'toolbox', name: 'Toolbox' },
    { id: 'contacts', name: 'Contacts' },
    { id: 'users', name: 'Users' },
];

const categoryColours: Record<string, string> = {
    account: '#5c6bc0',
    security: '#c62828',
    subscription: '#2e7d32',
    organization: '#00838f',
    toolbox: '#6a1b9a',
    contacts: '#ef6c00',
    users: '#37474f',
};

/**
 * A value as it should read in a change set. Nulls and blanks are the common case — a field
 * filled in for the first time, or cleared — and rendering them as nothing at all makes the row
 * unreadable, so they are named.
 */
function renderValue(value: unknown): string {
    if (value === null || value === undefined || value === '') {
        return '—';
    }
    if (typeof value === 'boolean') {
        return value ? 'yes' : 'no';
    }
    if (typeof value === 'object') {
        return JSON.stringify(value);
    }

    return String(value);
}

function ChangeList(record: ActivityRecord): ReactElement {
    const changes: ActivityChange[] = record.changeSet ?? [];
    const context: ActivityContextEntry[] = record.contextEntries ?? [];

    if (changes.length === 0 && context.length === 0) {
        return <Typography variant="body2" color="text.secondary">{ '—' }</Typography>;
    }

    return (
        <Box sx={ { display: 'flex', flexDirection: 'column', gap: 0.25 } }>
            { changes.map((change: ActivityChange, index: number): ReactElement => (
                <Typography key={ `change_${ record.id }_${ index }` } variant="body2">
                    <strong>{ change.label }</strong>
                    { ': ' }
                    { renderValue(change.from) }
                    { ' → ' }
                    { renderValue(change.to) }
                </Typography>
            )) }
            { context.map((entry: ActivityContextEntry): ReactElement => (
                <Typography key={ `context_${ record.id }_${ entry.key }` } variant="body2" color="text.secondary">
                    { `${ entry.label }: ${ renderValue(entry.value) }` }
                </Typography>
            )) }
        </Box>
    );
}

/**
 * The account's activity trail, newest first.
 *
 * Read-only by construction rather than by convention: the resource exposes Get and GetCollection
 * and nothing else, so there is no edit or delete to offer here.
 *
 * The rows are fetched through a filter rather than through a collection on the account.
 * CustomerAccount has no `activities` property on purpose — it is a bare ApiResource with no
 * serialization groups, so a mapped collection there would attach an unbounded list of IRIs to
 * every single GET of an account.
 */
export const CustomerAccountActivityPanel: () => JSX.Element = (): JSX.Element => {
    const record: CustomerAccount = useRecordContext();
    // ReferenceManyField takes a static `filter`, not the `filters` toolbar a List does, so the
    // category dropdown is driven from here rather than by react-admin's own filter machinery.
    const [category, setCategory] = useState<string>('');

    if (!record?.id) {
        return <></>;
    }

    function handleCategoryChange(event: ChangeEvent<HTMLInputElement>): void {
        setCategory(event.target.value);
    }

    return (
        <Box sx={ { width: '100%' } }>
            <Box sx={ { display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 } }>
                <Typography variant="body2" color="text.secondary">
                    { 'Everything this account\'s users have done, newest first. Records are never edited or deleted.' }
                </Typography>
                <MuiTextField
                    select
                    size="small"
                    label="Category"
                    value={ category }
                    onChange={ handleCategoryChange }
                    sx={ { minWidth: 200 } }
                >
                    <MenuItem value="">{ 'All categories' }</MenuItem>
                    { activityCategories.map((option: { id: string, name: string }): ReactElement => (
                        <MenuItem key={ option.id } value={ option.id }>{ option.name }</MenuItem>
                    )) }
                </MuiTextField>
            </Box>
            <ReferenceManyField
                label={ false }
                reference="customer_activity_monitors"
                target="customerAccount"
                sort={ { field: 'createdAt', order: 'DESC' } }
                perPage={ 25 }
                filter={ category === '' ? {} : { category } }
                pagination={ <Pagination rowsPerPageOptions={ [25, 50, 100] } /> }
            >
                <Datagrid bulkActionButtons={ false } sx={ { width: '100%' } }>
                    <DateField source="createdAt" label="When" showTime />
                    <FunctionField
                        label="Category"
                        render={ (activity: ActivityRecord): ReactElement => (
                            <Chip
                                size="small"
                                label={ activity.category }
                                sx={ {
                                    backgroundColor: categoryColours[activity.category] ?? '#607d8b',
                                    color: '#fff',
                                    textTransform: 'capitalize',
                                } }
                            />
                        ) }
                    />
                    <TextField source="typeLabel" label="Action" />
                    <FunctionField
                        label="Who"
                        render={ (activity: ActivityRecord): ReactElement => (
                            <Box>
                                <Typography variant="body2">{ activity.actorName }</Typography>
                                { activity.actorRoleLabel &&
                                    <Typography variant="caption" color="text.secondary">
                                        { activity.actorRoleLabel }
                                    </Typography>
                                }
                            </Box>
                        ) }
                    />
                    <FunctionField
                        label="Subject"
                        render={ (activity: ActivityRecord): ReactElement => (
                            <Typography variant="body2">
                                { activity.subjectLabel ?? '—' }
                            </Typography>
                        ) }
                    />
                    <FunctionField label="Detail" render={ ChangeList } />
                </Datagrid>
            </ReferenceManyField>
        </Box>
    );
}
